/**
 * Text-to-speech utility
 * Provider is locked to ElevenLabs voices listed in voices.md.
 * Fallback: browser SpeechSynthesis
 */
import { isSpeechSynthesisSupported } from './speechSynthesis';
import { preprocessLithuanianText } from './lithuanianTtsPreprocess.js';
import { API_BASE_URL } from '@/lib/apiBaseUrl.js';
import { supabase } from '@/lib/supabase.js';
import posthog from 'posthog-js';
import { assertAiSharingAllowed } from '@/native/compliance/aiConsentStore';
import {
  getSharedAudio,
  ensureAudioContextRunning,
  audioEnv,
  isIOS,
  routeAudioToSpeaker,
} from '@/pages/Timo/talk/audioUnlock.js';

// Track current audio/utterance for stopping
let currentAudio = null;
let currentUtterance = null;
// Active WebAudio playback: { stop() } . Set by playViaWebAudio, cleared by stopTTS.
let currentWebAudio = null;
// Active MSE streaming playback: { stop() }. Set by streamTalkViaMse (playTalk's
// low-latency tier), cleared by stopTTS or when the stream settles.
let currentMse = null;

// Keep in sync with voices.md (only these voices are usable in app).
const ALLOWED_ELEVENLABS_VOICE_IDS = [
  '9MQbSvZCz1PCthbJxqlc',
  'TpCiT74zy2gtqQN6PG8l',
];
const DEFAULT_ELEVENLABS_VOICE_ID = ALLOWED_ELEVENLABS_VOICE_IDS[0];

// BCP-47 voice code for the browser fallback voice. Every app language maps here
// explicitly, otherwise a degraded (browser voice) fallback speaks the reply with
// an English voice. Shared by playTTS and playTalk.
const SPEECH_LANG_MAP = {
  en: 'en-US',
  lt: 'lt-LT',
  ru: 'ru-RU',
  de: 'de-DE',
  no: 'nb-NO',
  es: 'es-ES',
  fr: 'fr-FR',
};

// MSE playback is only believed once currentTime actually advances. Three 300ms
// samples give the decoder up to ~900ms to produce sound before we give up and
// fall back to the buffered path. A false negative here is cheap (slower but
// still audible); a false positive is silence, which is what we are fixing.
const MSE_PROGRESS_STEP_MS = 300;
const MSE_PROGRESS_CHECKS = 3;
const MSE_PROGRESS_EPSILON = 0.01;

// Deadline for the /api/talk response HEADERS (not the streamed body). Production
// answers in 1.4 to 2.1s; 20s is a generous cold-start allowance, after which we
// stop waiting and speak with the browser voice instead of hanging in "thinking".
const TALK_HEADERS_TIMEOUT_MS = 20_000;

// Runaway-loop guard, not a usage budget. Tapping tiles to build a sentence, or
// replaying a phrase to practise it, is the core loop of the app: at 5 per
// minute a child hit the wall in about fifteen seconds and the button just went
// dead, since a tripped cooldown only writes to the console. 30 per minute is
// still a tap every two seconds sustained, which no human does by hand.
const CLIENT_TTS_LIMIT = 30;
const CLIENT_TTS_COOLDOWN_MS = 5_000;
const CLIENT_TTS_WINDOW_MS = 60_000;

const clientTtsGuard = {
  count: 0,
  windowStart: Date.now(),
  cooldownUntil: 0,
};

function checkClientTtsLimit() {
  const now = Date.now();

  if (now < clientTtsGuard.cooldownUntil) {
    return { allowed: false, retryAfterMs: clientTtsGuard.cooldownUntil - now };
  }

  if (now - clientTtsGuard.windowStart > CLIENT_TTS_WINDOW_MS) {
    clientTtsGuard.count = 0;
    clientTtsGuard.windowStart = now;
  }

  if (clientTtsGuard.count >= CLIENT_TTS_LIMIT) {
    clientTtsGuard.cooldownUntil = now + CLIENT_TTS_COOLDOWN_MS;
    clientTtsGuard.count = 0;
    clientTtsGuard.windowStart = now;
    return { allowed: false, retryAfterMs: CLIENT_TTS_COOLDOWN_MS };
  }

  clientTtsGuard.count += 1;
  return { allowed: true, retryAfterMs: 0 };
}

/** First bytes of a Uint8Array look like MP3 (frame sync or ID3 tag). */
function bytesLookLikeMp3(b) {
  if (!b || b.length < 2) return false;
  if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return true; // ID3
  return b[0] === 0xff && (b[1] & 0xe0) === 0xe0;
}

/** First bytes of an ArrayBuffer look like MP3 (frame sync or ID3 tag) */
function bufferLooksLikeMp3(arrayBuffer) {
  return bytesLookLikeMp3(
    new Uint8Array(arrayBuffer, 0, Math.min(4, arrayBuffer.byteLength))
  );
}

/* ------------------------------------------------------------------ */
/* Playback chain: WebAudio -> blessed <audio> -> speechSynthesis      */
/*                                                                     */
/* WebAudio is FIRST because of the iOS hardware ring/silent switch:   */
/* an HTMLAudioElement plays into the ambient audio session, which the */
/* physical mute switch silences with no error and no event, so the    */
/* code sees a perfect playback while the phone stays quiet. An        */
/* AudioBufferSourceNode on a gesture-unlocked AudioContext (kept out  */
/* of the ambient category by the silent session loop in audioUnlock)  */
/* keeps playing. Every tier records WHY it failed so the founder can  */
/* read the exact path on screen.                                      */
/* ------------------------------------------------------------------ */

// Report each distinct path once per page load so PostHog stays cheap.
const reportedAudioPaths = new Set();
function reportAudioPath(path, reason) {
  const key = `${path}:${reason || ''}`;
  if (reportedAudioPaths.has(key)) return;
  reportedAudioPaths.add(key);
  try {
    const env = audioEnv();
    posthog.capture('timo_audio_path', {
      path,
      reason: reason || null,
      isStandalone: env.isStandalone,
      isIOS: env.isIOS,
      ctxState: env.ctxState,
      elementBlessed: env.elementBlessed,
    });
  } catch {
    /* analytics must never break playback */
  }
}

function failure(reason, message) {
  const err = new Error(message || reason);
  err.audioReason = reason;
  return err;
}

/** decodeAudioData with the legacy callback signature as a fallback (old Safari). */
function decodeAudio(ctx, arrayBuffer) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const ok = (buf) => {
      if (settled) return;
      settled = true;
      if (buf) resolve(buf);
      else reject(failure('decode_empty', 'decodeAudioData returned nothing'));
    };
    const bad = (e) => {
      if (settled) return;
      settled = true;
      reject(failure('decode_failed', e?.message || 'decodeAudioData failed'));
    };
    try {
      const p = ctx.decodeAudioData(arrayBuffer, ok, bad);
      if (p && typeof p.then === 'function') p.then(ok, bad);
    } catch (e) {
      bad(e);
    }
  });
}

/**
 * Tier 1: WebAudio. Resolves { stopped } when playback ends or was cancelled.
 * Throws an Error carrying `.audioReason` on any failure.
 *
 * NOTE: an AudioBufferSourceNode couples rate and pitch, so the Lithuanian
 * slow-down is clamped here to keep Timo from sounding an octave down. A
 * slightly faster Timo is infinitely better than a silent one.
 */
async function playViaWebAudio(arrayBuffer, rate) {
  const ctx = await ensureAudioContextRunning();
  if (!ctx) throw failure('no_audiocontext', 'AudioContext unavailable');
  if (ctx.state !== 'running') throw failure(`ctx_${ctx.state}`, `AudioContext is ${ctx.state}`);

  // decodeAudioData detaches the buffer it is given, so hand it a copy and
  // leave the original intact for the <audio> element fallback below.
  const buffer = await decodeAudio(ctx, arrayBuffer.slice(0));

  return new Promise((resolve, reject) => {
    let source;
    try {
      source = ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = Math.max(0.94, Math.min(1, rate));
      const gain = ctx.createGain();
      gain.gain.value = 1;
      source.connect(gain);
      gain.connect(ctx.destination);
    } catch (e) {
      reject(failure('graph_failed', e?.message || 'could not build audio graph'));
      return;
    }

    let settled = false;
    const finish = (stopped) => {
      if (settled) return;
      settled = true;
      try { source.onended = null; } catch { /* non-fatal */ }
      if (currentWebAudio && currentWebAudio.source === source) currentWebAudio = null;
      resolve({ stopped });
    };

    source.onended = () => finish(false);
    currentWebAudio = {
      source,
      stop: () => {
        try { source.onended = null; } catch { /* non-fatal */ }
        try { source.stop(0); } catch { /* already stopped */ }
        finish(true);
      },
    };

    try {
      source.start(0);
    } catch (e) {
      currentWebAudio = null;
      settled = true;
      reject(failure('start_failed', e?.message || 'source.start failed'));
    }
  });
}

/**
 * Tier 2: the ONE pre-blessed HTMLAudioElement. Still useful when WebAudio
 * cannot decode the payload, and it is the only path on browsers without
 * decodeAudioData. On iOS it is mute-switch vulnerable, hence tier 2.
 */
function playViaElement(blob, rate) {
  return new Promise((resolve, reject) => {
    let url;
    try {
      url = URL.createObjectURL(blob);
    } catch (e) {
      reject(failure('objecturl_failed', e?.message || 'createObjectURL failed'));
      return;
    }
    const audio = getSharedAudio() || new Audio();
    // Claim the loudspeaker before this plays. Without it, any line after the
    // child's first turn comes out of the iPhone earpiece, because the mic left
    // the session in play-and-record. See audioUnlock.js.
    routeAudioToSpeaker();
    try {
      audio.setAttribute('playsinline', '');
      audio.preload = 'auto';
      audio.volume = 1;
    } catch { /* non-fatal */ }
    audio.src = url;
    audio.playbackRate = rate;
    currentAudio = audio;

    let settled = false;
    const detach = (stopped) => {
      if (settled) return;
      settled = true;
      audio.onended = null;
      audio.onerror = null;
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
      resolve({ stopped });
    };
    audio.onended = () => detach(false);
    audio.onerror = () => {
      if (settled) return;
      settled = true;
      audio.onended = null;
      audio.onerror = null;
      URL.revokeObjectURL(url);
      if (currentAudio === audio) currentAudio = null;
      reject(failure('element_error', 'audio element error event'));
    };

    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        if (settled) return;
        settled = true;
        audio.onended = null;
        audio.onerror = null;
        URL.revokeObjectURL(url);
        if (currentAudio === audio) currentAudio = null;
        const blocked =
          err &&
          (err.name === 'NotAllowedError' ||
            /gesture|user activation|not allowed/i.test(err.message || ''));
        reject(failure(blocked ? 'autoplay_blocked' : 'element_play_failed', err?.message));
      });
    }
  });
}

/**
 * Run the ordered playback chain over an already fully-buffered ArrayBuffer.
 * (Fully buffered is mandatory: decodeAudioData cannot work on a partial body.)
 *
 * @returns {Promise<{ ok: boolean, path: string, reason: string|null, stopped: boolean }>}
 *   ok=false means BOTH audio tiers failed and the caller should fall back to
 *   speechSynthesis, using `reason` as the degradation reason.
 */
async function playAudioChain(arrayBuffer, { rate = 1, mimeType = 'audio/mpeg', onAudioPath } = {}) {
  const emit = (path, reason) => {
    reportAudioPath(path, reason);
    try { onAudioPath?.({ path, reason: reason || null }); } catch { /* non-fatal */ }
  };

  // Build the blob BEFORE decoding: the Blob constructor copies the bytes, so
  // the element fallback survives decodeAudioData detaching its input.
  let blob = null;
  try {
    blob = new Blob([arrayBuffer], { type: mimeType });
  } catch { /* element tier will report it */ }

  let webAudioReason = 'unknown';
  try {
    const res = await playViaWebAudio(arrayBuffer, rate);
    emit('webaudio', null);
    return { ok: true, path: 'webaudio', reason: null, stopped: res.stopped };
  } catch (err) {
    webAudioReason = err?.audioReason || 'webaudio_failed';
    console.warn('[audio] WebAudio path failed:', webAudioReason, err?.message);
  }

  if (blob) {
    try {
      const res = await playViaElement(blob, rate);
      emit('element', webAudioReason);
      return { ok: true, path: 'element', reason: webAudioReason, stopped: res.stopped };
    } catch (err) {
      const elementReason = err?.audioReason || 'element_failed';
      console.warn('[audio] Element path failed:', elementReason, err?.message);
      emit('failed', `${webAudioReason}+${elementReason}`);
      return { ok: false, path: 'failed', reason: elementReason, stopped: false };
    }
  }

  emit('failed', `${webAudioReason}+no_blob`);
  return { ok: false, path: 'failed', reason: webAudioReason, stopped: false };
}

/** Playback rate per language, shared by every tier. */
function rateFor(language) {
  return language === 'lt' ? 0.88 : 0.95;
}

/* ------------------------------------------------------------------ */
/* MSE streaming tier (playTalk ONLY)                                  */
/*                                                                     */
/* /api/talk streams its mp3 chunked, but full-buffering it before     */
/* playback added seconds of dead air after the caption appeared.      */
/* MediaSource (desktop/Android) and ManagedMediaSource (iOS Safari    */
/* 17.1+) let the SAME pre-blessed <audio> element start speaking on   */
/* the first chunk. Element semantics apply (the silent session in     */
/* audioUnlock already handles the iOS mute switch). Old Safari never  */
/* touches any of this: every MSE reference lives inside               */
/* getMseConstructor() and only runs after detection succeeds.         */
/* ------------------------------------------------------------------ */

/**
 * The MediaSource constructor usable for streaming mp3, or null.
 * iOS Safari 17.1+ ships ManagedMediaSource instead of MediaSource; both are
 * driven the same way here (ManagedMediaSource additionally requires
 * disableRemotePlayback on the element). Guarded top to bottom so browsers
 * with neither (old iOS Safari) can never throw.
 */
function getMseConstructor() {
  try {
    if (typeof window === 'undefined') return null;
    const MSE = window.ManagedMediaSource || window.MediaSource;
    if (!MSE || typeof MSE.isTypeSupported !== 'function') return null;
    return MSE.isTypeSupported('audio/mpeg') ? MSE : null;
  } catch {
    return null;
  }
}

/**
 * Runtime kill switch for the MSE tier, so streaming can be turned off in a
 * live browser without a deploy:
 *   localStorage.setItem('ttt_no_mse', '1')   (sticky)
 *   ?nomse=1 in the URL                       (one session, also made sticky)
 * Returns the reason string when MSE must be skipped, or null when it may run.
 */
function mseDisabledReason() {
  try {
    if (typeof window === 'undefined') return 'no_window';
    const params = new URLSearchParams(window.location.search || '');
    const q = params.get('nomse');
    if (q === '1' || q === 'true') {
      try { window.localStorage.setItem('ttt_no_mse', '1'); } catch { /* non-fatal */ }
      return 'killswitch_query';
    }
    if (q === '0') {
      try { window.localStorage.removeItem('ttt_no_mse'); } catch { /* non-fatal */ }
    }
    try {
      if (window.localStorage.getItem('ttt_no_mse') === '1') return 'killswitch_localstorage';
    } catch { /* private mode: ignore */ }
    // iOS: the <audio> element (which MSE drives) is silenced by the hardware
    // ring/silent switch with no error at all, while WebAudio is not. The
    // buffered WebAudio chain is the proven-audible path on iPhone, so keep
    // streaming for desktop and Android only.
    if (isIOS()) return 'ios_prefers_webaudio';
    return null;
  } catch {
    return null;
  }
}

/** Concatenate streamed Uint8Array chunks into one ArrayBuffer. */
function concatChunks(chunks) {
  let total = 0;
  for (const c of chunks) total += c.byteLength;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out.buffer;
}

/**
 * Stream the /api/talk mp3 into the pre-blessed <audio> element via MSE,
 * starting playback on the first appended chunk.
 *
 * Never rejects. Resolves with one of:
 *   { status: 'played', stopped }
 *     Sound reached the child (full line, or partial after a mid-play failure,
 *     or stopTTS cut it: stopped=true means the caller must NOT fire onEnd).
 *   { status: 'failed', reason, chunks, streamDone, bodyLocked }
 *     GUARANTEED no sound was ever produced, so the caller can replay through
 *     the buffered chain without double-playing. `chunks` holds every
 *     Uint8Array read so far; `streamDone` says the body was fully consumed
 *     (replay the bytes, no refetch); `bodyLocked` false means the body was
 *     never touched and response.arrayBuffer() still works.
 */
function streamTalkViaMse(MSE, response, { rate = 1, onStarted } = {}) {
  return new Promise((resolve) => {
    const chunks = [];
    let streamDone = false;
    let sounded = false; // a 'playing' event fired: the child heard something
    let settled = false;
    let sniffed = false;
    let firstAppendDone = false;
    let url = null;
    let mediaSource = null;
    let sourceBuffer = null;
    let reader = null;
    let sourceOpenTimer = null;
    let endedGuardTimer = null;
    let progressTimer = null;

    const audio = getSharedAudio() || new Audio();
    // Same reason as the buffered path: the streaming path is the one the talk
    // loop uses most, so it is the one that would sound broken on an iPhone.
    routeAudioToSpeaker();
    const isManaged = Boolean(window.ManagedMediaSource && MSE === window.ManagedMediaSource);

    // Everything that can fail BEFORE the body gets locked happens first, so a
    // setup failure leaves response.arrayBuffer() usable for the old path.
    try {
      mediaSource = new MSE();
      url = URL.createObjectURL(mediaSource);
    } catch (e) {
      resolve({ status: 'failed', reason: 'mse_setup_failed', chunks, streamDone: false, bodyLocked: false });
      return;
    }

    try {
      reader = response.body.getReader();
    } catch (e) {
      try { URL.revokeObjectURL(url); } catch { /* non-fatal */ }
      resolve({ status: 'failed', reason: 'reader_failed', chunks, streamDone: false, bodyLocked: true });
      return;
    }

    // Read the rest of the body so a pre-sound failure can replay the complete
    // buffer through the classic chain without a second LLM + TTS round trip.
    const drainRemaining = async () => {
      if (streamDone) return true;
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) {
            streamDone = true;
            return true;
          }
          if (value && value.byteLength) chunks.push(value);
        }
      } catch {
        return false;
      }
    };

    const cleanup = () => {
      if (sourceOpenTimer) { clearTimeout(sourceOpenTimer); sourceOpenTimer = null; }
      if (endedGuardTimer) { clearTimeout(endedGuardTimer); endedGuardTimer = null; }
      if (progressTimer) { clearTimeout(progressTimer); progressTimer = null; }
      try { mediaSource.removeEventListener('sourceopen', onSourceOpen); } catch { /* non-fatal */ }
      try {
        audio.removeEventListener('playing', onPlaying);
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onElementError);
      } catch { /* non-fatal */ }
      if (isManaged) {
        try { audio.removeAttribute('disableremoteplayback'); } catch { /* non-fatal */ }
      }
      if (url) {
        try { URL.revokeObjectURL(url); } catch { /* non-fatal */ }
        url = null;
      }
      if (currentMse === handle) currentMse = null;
      if (currentAudio === audio) currentAudio = null;
    };

    const settlePlayed = (stopped) => {
      if (settled) return;
      settled = true;
      try { reader.cancel(); } catch { /* already done */ }
      cleanup();
      resolve({ status: 'played', stopped });
    };

    // Only ever called while sounded === false.
    const settleFailed = (reason) => {
      if (settled) return;
      settled = true;
      try { audio.pause(); } catch { /* non-fatal */ }
      cleanup();
      drainRemaining().then((done) => {
        resolve({ status: 'failed', reason, chunks, streamDone: done, bodyLocked: true });
      });
    };

    // Same safety idea as the element tier's caller-side guard: never strand
    // the SPEAKING state if 'ended' silently fails to fire. Once endOfStream()
    // ran, duration is knowable, so the timeout respects duration/playbackRate.
    const armEndedGuard = () => {
      if (settled || endedGuardTimer) return;
      let waitMs = 30000; // flat fallback when duration is unknowable
      try {
        const dur = Number.isFinite(audio.duration)
          ? audio.duration
          : Number.isFinite(mediaSource.duration) ? mediaSource.duration : NaN;
        if (Number.isFinite(dur) && dur > 0) {
          const effectiveRate = audio.playbackRate || rate || 1;
          waitMs = Math.max(0, ((dur - (audio.currentTime || 0)) / effectiveRate) * 1000) + 4000;
        }
      } catch { /* keep the flat fallback */ }
      endedGuardTimer = setTimeout(() => {
        if (sounded) settlePlayed(false);
        else settleFailed('ended_never_fired');
      }, Math.min(waitMs, 60000));
    };

    const endStreamSafely = () => {
      const finishStream = () => {
        if (settled) return;
        try {
          if (mediaSource.readyState === 'open') mediaSource.endOfStream();
          armEndedGuard();
        } catch {
          if (sounded) settlePlayed(false);
          else settleFailed('endofstream_failed');
        }
      };
      try {
        if (sourceBuffer && sourceBuffer.updating) {
          sourceBuffer.addEventListener('updateend', finishStream, { once: true });
        } else {
          finishStream();
        }
      } catch {
        finishStream();
      }
    };

    // A 'playing' event only means the element was allowed to start. It does not
    // mean the decoder produced anything: a malformed stream can fire 'playing'
    // and then sit at currentTime 0 forever. Because settleFailed() is reachable
    // only while sounded === false, trusting 'playing' would mark a silent stream
    // successful and permanently block the buffered fallback that does work.
    // So treat advancing currentTime as the only proof of playback.
    const armProgressWatchdog = () => {
      if (settled || sounded || progressTimer) return;
      let baseline = 0;
      try { baseline = audio.currentTime || 0; } catch { /* treat as 0 */ }
      let checks = 0;
      const tick = () => {
        progressTimer = null;
        if (settled || sounded) return;
        let now = 0;
        try { now = audio.currentTime || 0; } catch { /* treat as 0 */ }
        if (now > baseline + MSE_PROGRESS_EPSILON) {
          sounded = true;
          try { onStarted?.(); } catch { /* non-fatal */ }
          return;
        }
        checks += 1;
        if (checks >= MSE_PROGRESS_CHECKS) {
          settleFailed('no_progress');
          return;
        }
        progressTimer = setTimeout(tick, MSE_PROGRESS_STEP_MS);
      };
      progressTimer = setTimeout(tick, MSE_PROGRESS_STEP_MS);
    };

    const onPlaying = () => {
      armProgressWatchdog();
    };
    const onEnded = () => settlePlayed(false);
    const onElementError = () => {
      if (settled) return;
      if (sounded) settlePlayed(false);
      else settleFailed('element_error');
    };

    const waitForIdleBuffer = async () => {
      while (!settled && sourceBuffer && sourceBuffer.updating) {
        await new Promise((r) => setTimeout(r, 50));
      }
    };

    const appendChunk = (chunk) =>
      new Promise((res, rej) => {
        const onDone = () => {
          sourceBuffer.removeEventListener('updateend', onDone);
          sourceBuffer.removeEventListener('error', onErr);
          res();
        };
        const onErr = () => {
          sourceBuffer.removeEventListener('updateend', onDone);
          sourceBuffer.removeEventListener('error', onErr);
          rej(failure('sourcebuffer_error', 'SourceBuffer error event'));
        };
        sourceBuffer.addEventListener('updateend', onDone);
        sourceBuffer.addEventListener('error', onErr);
        try {
          sourceBuffer.appendBuffer(chunk);
        } catch (e) {
          sourceBuffer.removeEventListener('updateend', onDone);
          sourceBuffer.removeEventListener('error', onErr);
          rej(e);
        }
      });

    const appendWithQuotaRetry = async (chunk) => {
      for (let attempt = 0; attempt < 20; attempt += 1) {
        if (settled) return;
        await waitForIdleBuffer();
        if (settled) return;
        try {
          await appendChunk(chunk);
          return;
        } catch (e) {
          if (!e || e.name !== 'QuotaExceededError') throw e;
          // Buffer full: free what has already been heard, wait, retry.
          try {
            const played = Math.max(0, (audio.currentTime || 0) - 1);
            if (played > 0 && !sourceBuffer.updating) sourceBuffer.remove(0, played);
          } catch { /* non-fatal */ }
          await new Promise((r) => setTimeout(r, 350));
        }
      }
      throw failure('quota_stuck', 'SourceBuffer quota never freed');
    };

    // Called right after the FIRST successful append: this is what turns a
    // multi-second wait into near-instant speech.
    const startPlayback = () => {
      try { audio.playbackRate = rate; } catch { /* non-fatal */ }
      let playPromise = null;
      try {
        playPromise = audio.play();
      } catch (e) {
        settleFailed('element_play_failed');
        return;
      }
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          if (settled || sounded) return;
          const blocked =
            err &&
            (err.name === 'NotAllowedError' ||
              /gesture|user activation|not allowed/i.test(err.message || ''));
          settleFailed(blocked ? 'autoplay_blocked' : 'element_play_failed');
        });
      }
      // Also arm here, not just on 'playing': some engines never fire 'playing'
      // for a stream that fails to decode, and without this the tier would hang
      // until the ended-guard rather than falling back promptly.
      armProgressWatchdog();
    };

    const pump = async () => {
      for (;;) {
        if (settled) return;
        const { done, value } = await reader.read();
        if (settled) return;
        if (done) {
          streamDone = true;
          endStreamSafely();
          return;
        }
        if (!value || !value.byteLength) continue;
        if (!sniffed) {
          sniffed = true;
          // Cheap guard only; the real bufferLooksLikeMp3 checks stay on the
          // full-buffer path, which this failure falls back into.
          if (!bytesLookLikeMp3(value)) {
            chunks.push(value);
            settleFailed('stream_not_mp3');
            return;
          }
        }
        chunks.push(value);
        await appendWithQuotaRetry(value);
        if (settled) return;
        if (!firstAppendDone) {
          firstAppendDone = true;
          startPlayback();
        }
      }
    };

    const onSourceOpen = () => {
      if (sourceOpenTimer) { clearTimeout(sourceOpenTimer); sourceOpenTimer = null; }
      if (settled) return;
      try {
        sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
      } catch (e) {
        settleFailed('addsourcebuffer_failed');
        return;
      }
      pump().then(null, (e) => {
        if (settled) return;
        console.warn('[audio] MSE pump failed:', e?.audioReason || e?.message);
        if (sounded) {
          // The child already heard the start of the line: play out whatever is
          // buffered and count the line as spoken. NEVER replay from the top.
          endStreamSafely();
        } else {
          settleFailed(e?.audioReason || 'mse_stream_failed');
        }
      });
    };

    // stopTTS path (user interrupt / next line starting): resolve as
    // stopped so the caller skips onEnd, exactly like the other tiers.
    const handle = {
      stop: () => {
        try { audio.pause(); } catch { /* non-fatal */ }
        try { audio.currentTime = 0; } catch { /* non-fatal */ }
        settlePlayed(true);
      },
    };

    try {
      if (isManaged) {
        // ManagedMediaSource (iOS 17.1+) refuses to attach unless remote
        // playback is disabled on the element. Removed again in cleanup().
        audio.disableRemotePlayback = true;
        audio.setAttribute('disableremoteplayback', '');
      }
      audio.setAttribute('playsinline', '');
      audio.preload = 'auto';
      audio.volume = 1;
    } catch { /* non-fatal */ }

    mediaSource.addEventListener('sourceopen', onSourceOpen);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onElementError);

    currentMse = handle;
    currentAudio = audio;

    audio.src = url;
    try { audio.playbackRate = rate; } catch { /* non-fatal */ }

    // If sourceopen never fires, do not hang the turn: fail over to the
    // buffered chain (the drain in settleFailed keeps the download).
    sourceOpenTimer = setTimeout(() => {
      if (!settled && !sourceBuffer) settleFailed('sourceopen_timeout');
    }, 4000);
  });
}

/**
 * Speak with browser API; returns a Promise that settles when playback finishes.
 */
function speakWithSpeechSynthesis(trimmedText, speechLang, onEnd, onError) {
  return new Promise((resolve, reject) => {
    if (!isSpeechSynthesisSupported()) {
      reject(new Error('Speech synthesis not supported'));
      return;
    }
    const utterance = new SpeechSynthesisUtterance(trimmedText);
    utterance.lang = speechLang;
    utterance.rate = 0.8;
    utterance.onend = () => {
      currentUtterance = null;
      if (onEnd) onEnd();
      resolve();
    };
    utterance.onerror = (ev) => {
      currentUtterance = null;
      if (onError) onError(ev);
      reject(ev?.error ? new Error(String(ev.error)) : new Error('Speech synthesis error'));
    };
    currentUtterance = utterance;
    window.speechSynthesis.speak(utterance);
  });
}

/**
 * Stop any currently playing TTS
 */
export function stopTTS() {
  // Stop browser TTS
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
  
  // Stop WebAudio playback (the primary path). Its promise resolves with
  // stopped=true so the caller knows not to fire onEnd for a cancelled line.
  if (currentWebAudio) {
    const handle = currentWebAudio;
    currentWebAudio = null;
    try { handle.stop(); } catch { /* non-fatal */ }
  }

  // Stop MSE streaming playback (playTalk's low-latency tier). Resolves its
  // play promise with stopped=true, cancels the body reader and revokes the
  // MediaSource objectURL; it also clears currentAudio for its element, so
  // the block below only handles the classic blob-src element path.
  if (currentMse) {
    const handle = currentMse;
    currentMse = null;
    try { handle.stop(); } catch { /* non-fatal */ }
  }

  // Stop audio playback. The element may be the shared, pre-blessed singleton
  // (reused across turns) or a one-off Audio(); either way pausing + clearing
  // handlers is safe. Only revoke blob: URLs we created (never data: URIs).
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch { /* non-fatal */ }
    currentAudio.onended = null;
    currentAudio.onerror = null;
    const src = currentAudio.src || '';
    if (src.startsWith('blob:')) {
      URL.revokeObjectURL(src);
    }
    currentAudio = null;
  }

  currentUtterance = null;
}

/**
 * Play text-to-speech using locked ElevenLabs voices
 * @param {string} text - Text to speak
 * @param {string|object} languageOrOptions - Language code ('en' or 'lt') OR options object
 * @param {string} model - Legacy model argument (ignored; ElevenLabs is enforced)
 */
export async function playTTS(text, languageOrOptions = 'en', model = 'elevenlabs') {
  // Single chokepoint for ElevenLabs, so one guard covers every caller. See the
  // AI consent gate: no audio text leaves the app until the user has agreed.
  //
  // THROWS, and must keep throwing. This function is async, so a bare `return`
  // here handed every caller a RESOLVED promise: push-to-talk's SPEAK state sat
  // forever waiting for an onEnd that could not come (there is no SPEAK
  // timeout), and the non-verbal tile board ran its success branch, so a child
  // tapped Speak, heard nothing, saw no error, and watched the button lock for
  // five seconds. Rejecting is the one signal every caller already handles as
  // "the audio did not play", which is exactly what happened.
  assertAiSharingAllowed();

  // Parse arguments - support both old format and new options format
  let language, onEnd, onError, onFallback, onAudioPath, requestedModel, requestedVoiceId, bypassClientLimit, fast;

  if (typeof languageOrOptions === 'object' && languageOrOptions !== null) {
    // New format: options object
    language = languageOrOptions.lang || 'en';
    onEnd = languageOrOptions.onEnd;
    onError = languageOrOptions.onError;
    // Fired when we degrade to robotic browser speech, so the UI can tell the
    // user the ElevenLabs voice is unavailable instead of silently swapping.
    onFallback = languageOrOptions.onFallback;
    // Fired with { path, reason } as soon as the playback chain settles on a
    // tier, so the overlay can print "audio: webaudio" on the founder's phone.
    onAudioPath = languageOrOptions.onAudioPath;
    requestedModel = languageOrOptions.model;
    requestedVoiceId = languageOrOptions.voiceId;
    bypassClientLimit = languageOrOptions.bypassClientLimit === true;
    // Trade a little voice richness for a much shorter silence. Worth it on
    // tap-to-speak surfaces, not on the onboarding voice preview.
    fast = languageOrOptions.fast === true;
  } else {
    // Old format: (text, language, model)
    language = languageOrOptions;
    requestedModel = model;
    bypassClientLimit = false;
    fast = false;
  }
  
  // Enforce ElevenLabs as the only usable provider.
  // Browser speech remains fallback-only on failure.
  const ttsModel = 'elevenlabs';
  const storedVoiceId = localStorage.getItem('ttt-tts-voice-id');
  const candidateVoiceId = requestedVoiceId || storedVoiceId || DEFAULT_ELEVENLABS_VOICE_ID;
  const voiceId = ALLOWED_ELEVENLABS_VOICE_IDS.includes(candidateVoiceId)
    ? candidateVoiceId
    : DEFAULT_ELEVENLABS_VOICE_ID;

  // Return early if text is falsy or only whitespace
  if (!text || typeof text !== 'string' || text.trim().length === 0) {
    return;
  }

  // Lithuanian: local shaping matches server `/api/tts` (same `preprocessLithuanianText`).
  // Raw text is sent to the API; the server applies shaping so it cannot be skipped.
  const rawText = text.trim();
  const processedText =
    language === 'lt' ? preprocessLithuanianText(rawText) : rawText;
  // The /api/tts body still sends `language` raw; SPEECH_LANG_MAP only affects the
  // browser-voice fallback path (the path the kid hits when ElevenLabs is down).
  const speechLang = SPEECH_LANG_MAP[language] || 'en-US';

  if (requestedModel === 'browser') {
    console.warn('playTTS browser model ignored: ElevenLabs is enforced');
  }

  // Single degradation path: signal the caller (so the UI can show a "voice
  // resting" note), then speak with the robotic browser voice. onEnd still
  // fires when that voice finishes so the caller's state stays in sync with
  // audio instead of flipping to idle mid-speech. onError only fires if the
  // fallback itself cannot speak, so the caller is never left hanging.
  const runBrowserFallback = async (reason) => {
    if (typeof onFallback === 'function') {
      // Pass the machine-readable reason (e.g. 'quota', 'auth', 'all_keys_failed',
      // 'autoplay_blocked', 'network') so the caller can surface it on screen
      // instead of leaving the founder staring at silence.
      try { onFallback(reason); } catch { /* non-fatal */ }
    }
    // Tier 3 of the chain. Recorded even when it cannot run, so "no sound at
    // all" is still visible on screen and in PostHog rather than silent.
    const speechPath = isSpeechSynthesisSupported() ? 'speech' : 'none';
    reportAudioPath(speechPath, reason);
    try { onAudioPath?.({ path: speechPath, reason: reason || null }); } catch { /* non-fatal */ }
    if (!isSpeechSynthesisSupported()) {
      console.error('Browser TTS not supported, cannot play audio. Reason:', reason);
      if (onError) onError(new Error(`TTS unavailable (${reason || 'unknown'})`));
      return;
    }
    try {
      await speakWithSpeechSynthesis(processedText, speechLang, onEnd, null);
    } catch (e) {
      console.error('Browser TTS fallback failed:', e);
      if (onError) onError(e instanceof Error ? e : new Error('Browser TTS failed'));
    }
  };

  if (!bypassClientLimit) {
    const clientLimitCheck = checkClientTtsLimit();
    if (!clientLimitCheck.allowed) {
      const retrySeconds = Math.ceil(clientLimitCheck.retryAfterMs / 1000);
      console.warn(`TTS cooldown active: wait ${retrySeconds}s before next listen.`);
      if (onError) onError(new Error(`TTS cooldown active. Retry in ${retrySeconds}s`));
      return;
    }
  }

  // API-based TTS model (ElevenLabs)
  try {
    // Stop any existing TTS first
    stopTTS();

    // Same-origin `/api` in dev (Vite proxy); production uses shared API base.
    const ttsUrl = `${API_BASE_URL}/api/tts`;

    console.log(`Calling ${ttsModel} TTS endpoint:`, ttsUrl);

    // Attach Supabase JWT so the backend auth gate can identify the user.
    // Subscribers/promo-redeemers get full quota; logged-in-only users get a
    // small preview quota (for onboarding voice picker). Anonymous = 401.
    // We read the session RIGHT BEFORE the call (not a stale cached token) and,
    // if the backend still 401s, refresh once and retry - a token that expired
    // mid-session was a real silent-failure cause on long phone sessions.
    const postTts = async (token) => {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      return fetch(ttsUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: processedText, language, voiceId, fast }),
      });
    };

    let accessToken = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      accessToken = session?.access_token || null;
    } catch (sessionErr) {
      console.warn('TTS: could not read Supabase session, calling without auth:', sessionErr?.message);
    }

    let response = await postTts(accessToken);

    if (response.status === 401) {
      console.warn('TTS: 401 from /api/tts, refreshing session token and retrying once.');
      try {
        const { data: { session: refreshed } } = await supabase.auth.refreshSession();
        const freshToken = refreshed?.access_token || null;
        if (freshToken && freshToken !== accessToken) {
          response = await postTts(freshToken);
        }
      } catch (refreshErr) {
        console.warn('TTS: session refresh failed:', refreshErr?.message);
      }
    }

    // Handle non-OK responses - fallback to browser TTS, carrying the reason.
    if (!response.ok) {
      const responseText = await response.text().catch(() => '');
      let errorMessage = 'Unknown error';
      let parsedError = null;
      if (responseText) {
        try {
          parsedError = JSON.parse(responseText);
          errorMessage = parsedError?.error || responseText;
        } catch {
          errorMessage = responseText;
        }
      }
      // Backend sends a machine-readable `code` (auth | quota | all_keys_failed |
      // global_cap | cooldown). Fall back to an HTTP-status-derived reason.
      const reason = parsedError?.code
        || (response.status === 401 ? 'auth'
          : response.status === 402 || response.status === 403 ? 'quota'
          : response.status === 503 ? 'global_cap'
          : `http_${response.status}`);

      if (response.status === 429) {
        const retryAfterHeader = Number(response.headers.get('Retry-After') || 0);
        const retryAfter = Number(parsedError?.retryAfter || 0) || retryAfterHeader || 10;
        console.warn(`TTS rate/cooldown limit hit. Retry in ${retryAfter}s.`);
        // Rate-limit is transient: speak it with the browser voice rather than
        // going silent, and tell the caller why.
        await runBrowserFallback('cooldown');
        if (onError) onError(new Error(`TTS limit hit. Retry in ${retryAfter}s`));
        return;
      }

      console.warn(`${ttsModel} TTS failed (${reason}), falling back to browser TTS:`, response.status, errorMessage);
      await runBrowserFallback(reason);
      return;
    }

    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    // ArrayBuffer, not blob: the WebAudio path needs the raw bytes, and reading
    // the body to completion here is exactly what decodeAudioData requires.
    const arrayBuffer = await response.arrayBuffer();

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      console.error('TTS: empty body from /api/tts (is the backend running and ELEVENLABS_API_KEY set?)');
      await runBrowserFallback('empty_audio');
      return;
    }

    const looksMp3 = bufferLooksLikeMp3(arrayBuffer);
    const nonAudioType = contentType.includes('json') || contentType.includes('text/html');
    if (!looksMp3 && (nonAudioType || arrayBuffer.byteLength < 400)) {
      const errBody = new TextDecoder().decode(arrayBuffer.slice(0, 400));
      console.error('TTS: expected MPEG audio but got:', contentType, errBody);
      await runBrowserFallback('bad_audio');
      return;
    }

    // Ordered chain: WebAudio (survives the iOS ring/silent switch) -> the
    // pre-blessed HTMLAudioElement -> the robotic browser voice below.
    const result = await playAudioChain(arrayBuffer, {
      rate: rateFor(language),
      mimeType: contentType.includes('audio') ? contentType : 'audio/mpeg',
      onAudioPath,
    });

    if (result.ok) {
      if (!result.stopped && onEnd) onEnd();
      return;
    }

    console.error('TTS: every audio path failed, trying browser speech:', result.reason);
    await runBrowserFallback(result.reason || 'audio_error');
  } catch (error) {
    console.error(`Error in ${ttsModel} TTS (network or server unreachable?):`, error);
    await runBrowserFallback('network');
  }
}

/**
 * playTalk - combined LLM-reply + TTS in ONE round trip (Talking Timo live loop).
 *
 * Calls POST /api/talk, which runs the timo_talk model and streams the flash TTS
 * audio back in the same response. The reply text comes back on the X-Reply-Text
 * header so the caller can caption the moment the response headers arrive, while
 * the audio plays through the SAME pre-blessed <audio> element playTTS uses.
 *
 * Never silent: if audio cannot be produced or played, the reply text (from the
 * header or the JSON error body) is spoken with the robotic browser voice and the
 * failure `reason` is handed to onFallback so it can be shown on screen.
 *
 * @returns {Promise<{ replyText: string }>}
 */
export async function playTalk({
  newMessage,
  language = 'en',
  messages = [],
  voiceId: requestedVoiceId,
  wordOfDay = null,
  onReplyText,
  onEnd,
  onError,
  onFallback,
  onAudioPath,
} = {}) {
  const text = typeof newMessage === 'string' ? newMessage.trim() : '';
  if (!text) {
    onError?.(new Error('playTalk: empty message'));
    return { replyText: '' };
  }

  // /api/talk sends the child's words AND the conversation history to the LLM
  // provider, then streams ElevenLabs audio back, so this one call reaches two
  // processors at once. playTTS guards the TTS-only path; this is the guard for
  // the combined path, and without it the whole talk loop bypassed consent.
  //
  // Throws for the same reason playTTS does: resolving with an empty reply is a
  // shape that also means "the model said nothing", and the two must not be
  // confused. useTalkLoop wraps this call in a try/catch, so the rejection lands
  // there and its existing fallback runs.
  assertAiSharingAllowed();

  const storedVoiceId = localStorage.getItem('ttt-tts-voice-id');
  const candidateVoiceId = requestedVoiceId || storedVoiceId || DEFAULT_ELEVENLABS_VOICE_ID;
  const voiceId = ALLOWED_ELEVENLABS_VOICE_IDS.includes(candidateVoiceId)
    ? candidateVoiceId
    : DEFAULT_ELEVENLABS_VOICE_ID;
  const speechLang = SPEECH_LANG_MAP[language] || 'en-US';

  let replyText = '';
  let captioned = false;
  const emitReply = (t) => {
    const clean = (t || '').toString().trim();
    if (!clean || captioned) return;
    captioned = true;
    replyText = clean;
    try { onReplyText?.(clean); } catch { /* non-fatal */ }
  };

  // Speak the reply with the robotic browser voice; never leaves the child in
  // silence. `reason` is surfaced so the founder can read why on the phone.
  const browserFallback = async (reason) => {
    try { onFallback?.(reason); } catch { /* non-fatal */ }
    const toSpeak = (replyText || '').trim();
    const speechPath = isSpeechSynthesisSupported() && toSpeak ? 'speech' : 'none';
    reportAudioPath(speechPath, reason);
    try { onAudioPath?.({ path: speechPath, reason: reason || null }); } catch { /* non-fatal */ }
    if (!toSpeak) {
      onError?.(new Error(`talk failed (${reason}) with no reply text`));
      return;
    }
    if (!isSpeechSynthesisSupported()) {
      onError?.(new Error(`TTS unavailable (${reason})`));
      return;
    }
    const processed = language === 'lt' ? preprocessLithuanianText(toSpeak) : toSpeak;
    try {
      await speakWithSpeechSynthesis(processed, speechLang, onEnd, null);
    } catch (e) {
      onError?.(e instanceof Error ? e : new Error('Browser TTS failed'));
    }
  };

  try {
    stopTTS();
    const talkUrl = `${API_BASE_URL}/api/talk`;

    // Hard deadline on the RESPONSE HEADERS only. The body is a stream we want to
    // keep reading, so the abort timer is cleared the moment headers land. Without
    // this, a stalled request leaves the child staring at a thinking Timo forever.
    const postTalk = async (token) => {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = controller
        ? setTimeout(() => { try { controller.abort(); } catch { /* non-fatal */ } }, TALK_HEADERS_TIMEOUT_MS)
        : null;
      try {
        return await fetch(talkUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ newMessage: text, language, messages, voiceId, wordOfDay }),
          signal: controller ? controller.signal : undefined,
        });
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    let accessToken = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      accessToken = session?.access_token || null;
    } catch (sessionErr) {
      console.warn('talk: could not read Supabase session:', sessionErr?.message);
    }

    let response = await postTalk(accessToken);
    if (response.status === 401) {
      console.warn('talk: 401 from /api/talk, refreshing token and retrying once.');
      try {
        const { data: { session: refreshed } } = await supabase.auth.refreshSession();
        const freshToken = refreshed?.access_token || null;
        if (freshToken && freshToken !== accessToken) {
          accessToken = freshToken;
          response = await postTalk(freshToken);
        }
      } catch (refreshErr) {
        console.warn('talk: session refresh failed:', refreshErr?.message);
      }
    }

    // The reply text rides on a response header for the audio-success path.
    const headerReply = response.headers.get('X-Reply-Text');
    if (headerReply) {
      try { emitReply(decodeURIComponent(headerReply)); } catch { emitReply(headerReply); }
    }

    if (!response.ok) {
      const bodyText = await response.text().catch(() => '');
      let parsed = null;
      try { parsed = JSON.parse(bodyText); } catch { /* non-JSON body */ }
      // Non-audio failure paths still include the reply text in the JSON body.
      if (parsed?.replyText) emitReply(parsed.replyText);
      const reason = parsed?.code
        || (response.status === 401 ? 'auth'
          : response.status === 402 || response.status === 403 ? 'quota'
          : response.status === 503 ? 'global_cap'
          : `http_${response.status}`);
      if (response.status === 429) {
        const retryAfter = Number(parsed?.retryAfter || response.headers.get('Retry-After') || 10);
        await browserFallback('cooldown');
        onError?.(new Error(`talk limit hit. Retry in ${retryAfter}s`));
        return { replyText };
      }
      console.warn(`talk failed (${reason}); browser-voice fallback.`, response.status);
      await browserFallback(reason);
      return { replyText };
    }

    // A JSON 200 is impossible today, but if the server ever returns non-audio on
    // a 200 (e.g. a text-only reply), speak it rather than trying to decode audio.
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('json') || contentType.includes('text/html')) {
      const bodyText = await response.text().catch(() => '');
      let parsed = null;
      try { parsed = JSON.parse(bodyText); } catch { /* ignore */ }
      if (parsed?.replyText) emitReply(parsed.replyText);
      await browserFallback(parsed?.code || 'no_audio');
      return { replyText };
    }

    // MSE streaming tier: /api/talk streams the mp3 chunked, so start speaking
    // on the first chunk instead of sitting in dead air while the whole body
    // downloads. Browsers without (Managed)MediaSource, and any failure BEFORE
    // sound started, drop to the classic full-buffer chain below.
    const mseSkip = mseDisabledReason();
    const MSE = mseSkip ? null : getMseConstructor();
    if (mseSkip) {
      try { onAudioPath?.({ path: 'mse_skipped', reason: mseSkip }); } catch { /* non-fatal */ }
    }
    let arrayBuffer = null;
    if (MSE && response.body && typeof response.body.getReader === 'function') {
      const mse = await streamTalkViaMse(MSE, response, {
        rate: rateFor(language),
        onStarted: () => {
          reportAudioPath('mse');
          try { onAudioPath?.({ path: 'mse', reason: null }); } catch { /* non-fatal */ }
        },
      });
      if (mse.status === 'played') {
        if (!mse.stopped) onEnd?.();
        return { replyText };
      }
      // streamTalkViaMse guarantees no sound was produced on 'failed', so
      // replaying through the buffered chain below cannot double-play.
      reportAudioPath('mse_failed', mse.reason);
      if (mse.streamDone && mse.chunks.length) {
        // The whole body is already in hand: reuse the bytes, no refetch.
        arrayBuffer = concatChunks(mse.chunks);
      } else if (mse.bodyLocked) {
        // Stream broke mid-download and the body is unusable: refetch once.
        console.warn('talk: MSE stream broke before sound, refetching once:', mse.reason);
        try {
          response = await postTalk(accessToken);
        } catch (refetchErr) {
          console.error('talk: refetch after MSE failure failed:', refetchErr);
          await browserFallback('network');
          return { replyText };
        }
        if (!response.ok) {
          await browserFallback(mse.reason || 'mse_failed');
          return { replyText };
        }
      }
      // bodyLocked === false means setup failed before touching the body, so
      // the original response.arrayBuffer() below still works.
    }

    // Buffer the whole body before decoding. decodeAudioData needs a COMPLETE
    // buffer, so on the non-streaming path this await is required.
    if (!arrayBuffer) arrayBuffer = await response.arrayBuffer();
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      await browserFallback('empty_audio');
      return { replyText };
    }
    if (!bufferLooksLikeMp3(arrayBuffer) && arrayBuffer.byteLength < 400) {
      await browserFallback('bad_audio');
      return { replyText };
    }

    // Ordered chain: WebAudio -> pre-blessed <audio> -> browser voice. WebAudio
    // leads because the iOS ring/silent switch mutes the element path without
    // raising a single error, which is what left Timo silent on a real iPhone.
    const result = await playAudioChain(arrayBuffer, {
      rate: rateFor(language),
      mimeType: contentType.includes('audio') ? contentType : 'audio/mpeg',
      onAudioPath,
    });

    if (result.ok) {
      if (!result.stopped) onEnd?.();
      return { replyText };
    }

    console.error('talk: every audio path failed, browser fallback:', result.reason);
    await browserFallback(result.reason || 'audio_error');
    return { replyText };
  } catch (error) {
    console.error('talk: network or server unreachable:', error);
    await browserFallback('network');
    return { replyText };
  }
}

/**
 * Helper: Create a WAV file from PCM audio data
 * Gemini returns raw PCM data that needs WAV headers to be playable
 */
function createWavBlob(pcmData, sampleRate = 24000, channels = 1) {
  const bytesPerSample = 2;
  const blockAlign = channels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  
  // Create WAV header
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  
  // "RIFF" chunk descriptor
  view.setUint8(0, 0x52); // 'R'
  view.setUint8(1, 0x49); // 'I'
  view.setUint8(2, 0x46); // 'F'
  view.setUint8(3, 0x46); // 'F'
  
  // File size - 8
  view.setUint32(4, 36 + pcmData.length, true);
  
  // "WAVE" format
  view.setUint8(8, 0x57);  // 'W'
  view.setUint8(9, 0x41);  // 'A'
  view.setUint8(10, 0x56); // 'V'
  view.setUint8(11, 0x45); // 'E'
  
  // "fmt " subchunk
  view.setUint8(12, 0x66); // 'f'
  view.setUint8(13, 0x6d); // 'm'
  view.setUint8(14, 0x74); // 't'
  view.setUint8(15, 0x20); // ' '
  
  // Subchunk1 size (16 for PCM)
  view.setUint32(16, 16, true);
  
  // Audio format (1 for PCM)
  view.setUint16(20, 1, true);
  
  // Number of channels
  view.setUint16(22, channels, true);
  
  // Sample rate
  view.setUint32(24, sampleRate, true);
  
  // Byte rate
  view.setUint32(28, byteRate, true);
  
  // Block align
  view.setUint16(32, blockAlign, true);
  
  // Bits per sample
  view.setUint16(34, 16, true);
  
  // "data" subchunk
  view.setUint8(36, 0x64); // 'd'
  view.setUint8(37, 0x61); // 'a'
  view.setUint8(38, 0x74); // 't'
  view.setUint8(39, 0x61); // 'a'
  
  // Subchunk2 size (actual audio data size)
  view.setUint32(40, pcmData.length, true);
  
  // Combine header and PCM data
  return new Blob([header, pcmData], { type: 'audio/wav' });
}

/**
 * Helper: Convert base64 to Blob
 */
function base64ToBlob(base64, mimeType) {
  try {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  } catch (error) {
    console.error('Error converting base64 to blob:', error);
    throw new Error('Failed to convert audio data');
  }
}

/**
 * Play text with Gemini TTS (Puck voice - good for English + Lithuanian)
 * @param {string} text - Text to speak
 * @param {string} language - Language code ('en' or 'lt')
 * @returns {Promise<Audio>} Audio object for playback control
 */
export async function playTTSGemini(text, language = 'en', retryCount = 0) {
  try {
    // /api/tts-gemini hands the text to Google. Nothing in src/ calls this today
    // (ElevenLabs is enforced through playTTS), but it is exported, so it is one
    // import away from being an unguarded path to a processor. Throws rather
    // than returning null: null is this function's "nothing to say" answer, and
    // "we refused to send it" must not look like that.
    assertAiSharingAllowed();

    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      return null;
    }

    const rawText = text.trim();
    const processedText =
      language === 'lt' ? preprocessLithuanianText(rawText) : rawText;
    console.log('🔊 Gemini TTS called:', rawText.substring(0, 50), `(attempt ${retryCount + 1})`);

    // Stop any existing TTS first
    stopTTS();

    if (language === 'lt' && typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('ttt-lithuanian-tts-preview', { detail: { text: processedText } })
      );
    }

    // Gemini key stays on the server; client calls rate-limited /api/tts-gemini (server shapes LT)
    const response = await fetch(`${API_BASE_URL}/api/tts-gemini`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: rawText, language }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const statusCode = response.status;
      const errorMsg =
        typeof errorData.error === 'string'
          ? errorData.error
          : errorData.error?.message || 'Unknown error';

      if ((statusCode === 429 || statusCode >= 500 || statusCode === 400) && retryCount < 2) {
        console.warn(`⚠️ Gemini TTS error ${statusCode}: ${errorMsg}, retrying...`);
        await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)));
        return playTTSGemini(text, language, retryCount + 1);
      }

      throw new Error(`Gemini TTS failed: ${statusCode} - ${errorMsg}`);
    }

    const audioBlob = await response.blob();
    const audioUrl = URL.createObjectURL(audioBlob);
    
    const audio = new Audio(audioUrl);
    currentAudio = audio;

    // Handle playback errors
    audio.onerror = (e) => {
      console.error('Error playing Gemini audio:', e);
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;
    };

    // Clean up on end
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
      currentAudio = null;
    };
    
    await audio.play();
    console.log('✅ Gemini TTS playing');
    
    return audio;
    
  } catch (error) {
    console.error('❌ Gemini TTS error:', error);
    throw error;
  }
}

